import { Controller } from 'stimulus'
import {
  controlChart, fieldStrip, histogram, binsHistogram, weeklyWhiskers,
  kpiTile, renderTable, fmtNum
} from '../lib/metrics/charts'

// Drives the Trends tab. Task-first: picking a task loads a Results-Summary
// style strip for EVERY numeric field on that lineage — one dot is one
// sample's average across jobs, red when any of its results failed (stored
// status), pale under 3 results, a diamond when it's a statistical outlier.
// Clicking a field row drills into the control chart / distribution /
// failed-results cards below; there is no separate field picker.
export default class extends Controller {
  static targets = [
    'range', 'task', 'traitPicker', 'valuePicker', 'chips',
    'strips', 'stripsSub',
    'kpiN', 'kpiMean', 'kpiSd', 'kpiPass',
    'chartSub', 'chart', 'chartTable', 'hist', 'histSub', 'failed',
    'crit', 'critSub', 'jobs', 'jobsSub'
  ];

  connect() {
    this.traitFilters = []
    this.field = null
    this.allTaskOptions = [...this.taskTarget.options].slice(1)
      .map(o => ({ value: o.value, label: o.textContent }))
    this.initFromUrl()
    this.onShow = () => this.load()
    window.addEventListener('metrics:trends-shown', this.onShow)
    // Direct ?tab=trends navigation (saved views, shared URLs): the outer
    // metrics controller activates this panel during ITS connect(), which
    // runs before this listener registers — the shown event is already
    // gone. The active class survives, so load from it.
    if (this.element.classList.contains('active')) this.load()
    this.resizeHandler = () => {
      clearTimeout(this.resizeTimer)
      this.resizeTimer = setTimeout(() => {
        if (this.element.offsetParent !== null) this.load()
      }, 160)
    }
    window.addEventListener('resize', this.resizeHandler)
  }

  disconnect() {
    window.removeEventListener('metrics:trends-shown', this.onShow)
    window.removeEventListener('resize', this.resizeHandler)
  }

  initFromUrl() {
    const url = new URLSearchParams(window.location.search)
    if (url.get('tab') !== 'trends') return
    if (url.get('range')) this.rangeTarget.value = url.get('range')
    url.getAll('traits[]').forEach((raw) => {
      const [rawKey, value] = raw.split(/:(.*)/s)
      const traitId = decodeURIComponent(rawKey || '')
      if (!traitId || !value) return
      const opt = [...this.traitPickerTarget.options].find(o => o.value === traitId)
      this.traitFilters.push({ traitId, traitName: opt ? opt.textContent : traitId, value })
    })
    if (this.traitFilters.length) this.renderChips()
    if (url.get('task')) {
      this.taskTarget.value = url.get('task')
      this.pendingField = url.get('field')
      this.load()
    }
  }

  viewParams() {
    const params = { tab: 'trends', range: this.rangeTarget.value }
    if (this.taskTarget.value) params.task = this.taskTarget.value
    if (this.field) params.field = this.field
    if (this.traitFilters.length) params.traits = this.traitFilters.map(f => `${encodeURIComponent(f.traitId)}:${f.value}`)
    return params
  }

  // Guarded responses belonging to a superseded load() are dropped, so a
  // slow older request can't paint over a newer one. Picker fetches pass
  // guarded: false — they populate dropdowns, not cards.
  fetchJson(url, onData, guarded = true) {
    const seq = this.loadSeq || 0
    fetch(url, { headers: { Accept: 'application/json' } })
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(r.status))))
      .then((data) => {
        if (guarded && seq !== (this.loadSeq || 0)) return
        onData(data)
      })
      .catch(() => {})
  }

  taskChosen() {
    this.field = null
    this.load()
  }

  // ---- trait chips (same interaction as the results tab) ----

  traitChosen() {
    const traitId = this.traitPickerTarget.value
    const picker = this.valuePickerTarget
    picker.innerHTML = ''
    if (!traitId) {
      picker.disabled = true
      picker.appendChild(new Option('Pick a trait first', ''))
      return
    }
    picker.disabled = true
    picker.appendChild(new Option('Loading…', ''))
    this.fetchJson(`${this.data.get('traitValuesUrl')}?${new URLSearchParams({ trait: traitId })}`, (data) => {
      picker.innerHTML = ''
      picker.appendChild(new Option('Pick a value…', ''))
      data.values.forEach(v => picker.appendChild(new Option(v, v)))
      picker.disabled = false
    }, false)
  }

  addTrait() {
    const { value } = this.valuePickerTarget
    const traitId = this.traitPickerTarget.value
    if (!value || !traitId) return
    const traitName = this.traitPickerTarget.selectedOptions[0].textContent
    if (!this.traitFilters.some(f => f.traitId === traitId && f.value === value)) {
      this.traitFilters.push({ traitId, traitName, value })
    }
    this.traitPickerTarget.value = ''
    this.traitChosen()
    this.renderChips()
    this.load()
  }

  removeTrait(ev) {
    this.traitFilters.splice(Number(ev.currentTarget.dataset.index), 1)
    this.renderChips()
    this.load()
  }

  renderChips() {
    const host = this.chipsTarget
    host.innerHTML = ''
    this.traitFilters.forEach((f, i) => {
      const chip = document.createElement('span')
      chip.className = 'm-chip'
      const label = document.createElement('span')
      label.textContent = `${f.traitName}: `
      const strong = document.createElement('strong')
      strong.textContent = f.value
      label.appendChild(strong)
      chip.appendChild(label)
      const x = document.createElement('button')
      x.type = 'button'
      x.textContent = '✕'
      x.title = 'Remove filter'
      x.dataset.index = i
      x.dataset.action = 'click->metrics-trends#removeTrait'
      chip.appendChild(x)
      host.appendChild(chip)
    })
  }

  // ---- load & render ----

  load() {
    this.loadSeq = (this.loadSeq || 0) + 1
    this.refreshTaskOptions()
    const task = this.taskTarget.value
    if (!task) {
      this.stripsTarget.innerHTML = ''
      this.stripsSubTarget.textContent = 'Pick a task — every numeric result measurement on it renders as a strip of samples'
      this.renderEmpty('Pick a task above')
      return
    }
    const p = new URLSearchParams({ range: this.rangeTarget.value, task })
    this.traitFilters.forEach(f => p.append('traits[]', `${encodeURIComponent(f.traitId)}:${f.value}`))
    this.fetchJson(`${this.data.get('stripsUrl')}?${p.toString()}`, data => this.renderStrips(data))
  }

  // The task picker only offers tasks with numeric values in the selected
  // range; the current selection survives even when it falls outside.
  refreshTaskOptions() {
    const p = new URLSearchParams({ range: this.rangeTarget.value })
    this.fetchJson(`${this.data.get('tasksUrl')}?${p.toString()}`, (data) => {
      const available = new Set(data.tasks)
      const current = this.taskTarget.value
      const options = this.allTaskOptions.filter(o => available.has(o.value) || o.value === current)
      const key = options.map(o => o.value).join('|')
      if (key === this.taskOptionsKey) return
      this.taskOptionsKey = key
      this.taskTarget.innerHTML = ''
      this.taskTarget.appendChild(new Option('Pick a task…', ''))
      options.forEach(o => this.taskTarget.appendChild(new Option(o.label, o.value)))
      this.taskTarget.value = current
    }, false)
  }

  renderStrips(data) {
    const host = this.stripsTarget
    host.innerHTML = ''
    this.stripFields = data.fields.map(f => f.name)
    if (!data.fields.length) {
      this.stripsSubTarget.textContent = 'No numeric result measurements on this task in range'
      this.renderEmpty('No numeric result measurements to trend on this task')
      return
    }
    if (this.pendingField && this.stripFields.includes(this.pendingField)) this.field = this.pendingField
    this.pendingField = null
    if (!this.field || !this.stripFields.includes(this.field)) [this.field] = this.stripFields

    const taskLabel = this.taskTarget.selectedOptions[0].textContent
    this.stripsSubTarget.textContent = `${taskLabel} · one dot = one sample's average across jobs · red = has a failed result · pale = under 3 results · ◆ = outlier · click a measurement to open its trend`

    const wrap = document.createElement('div')
    wrap.className = 'm-fstrips'
    // attach BEFORE drawing: each strip SVG sizes itself from clientWidth,
    // and a detached container measures 0 → every strip falls back to 480px
    host.appendChild(wrap)
    data.fields.forEach((f) => {
      const outliers = this.outlierIds(f.points)
      const points = f.points.map(p => ({ ...p, outlier: outliers.has(p.sample_id) }))
      const judged = f.criteria_state !== 'none'
      const avgs = points.map(p => p.avg)
      const dp = avgs.some(v => !Number.isInteger(v)) ? 1 : 0

      const row = document.createElement('div')
      row.className = `m-frow${f.name === this.field ? ' sel' : ''}`
      row.dataset.field = f.name

      const meta = document.createElement('div')
      meta.className = 'm-fmeta'
      const nameEl = document.createElement('div')
      nameEl.className = 'm-fname'
      nameEl.textContent = f.name
      meta.appendChild(nameEl)
      const subEl = document.createElement('div')
      subEl.className = 'm-fsub'
      const bits = [`${fmtNum(f.samples_total)} samples`, `${fmtNum(f.results)} results`]
      if (f.samples_shown < f.samples_total) bits.push(`showing newest ${fmtNum(f.samples_shown)}`)
      if (f.criteria_state === 'varies') bits.push('criteria vary by definition')
      if (f.criteria_state === 'none') bits.push('no pass/fail criteria')
      subEl.textContent = bits.join(' · ')
      meta.appendChild(subEl)
      row.appendChild(meta)

      const viz = document.createElement('div')
      viz.className = 'm-fviz'
      row.appendChild(viz)

      const num = document.createElement('div')
      num.className = 'm-fnum'
      const mean = avgs.length ? avgs.reduce((a, b) => a + b, 0) / avgs.length : null
      const meanEl = document.createElement('div')
      meanEl.className = 'm-fmean'
      meanEl.textContent = mean === null ? '—' : fmtNum(mean, dp)
      num.appendChild(meanEl)
      const passEl = document.createElement('div')
      const fails = points.filter(p => p.fail).length
      if (judged && points.length) {
        passEl.className = `m-fpass ${fails ? 'bad' : 'good'}`
        passEl.textContent = fails
          ? `${Math.round((100 * (points.length - fails)) / points.length)}% pass · ${fails} fail`
          : '100% pass'
      } else {
        passEl.className = 'm-fpass na'
        passEl.textContent = judged ? '—' : 'not judged'
      }
      num.appendChild(passEl)
      row.appendChild(num)

      wrap.appendChild(row)
      fieldStrip(viz, points, {
        criteria: f.criteria,
        judged,
        dp,
        onOpen: (p) => { window.location.href = `/jobs/${p.job_id}/tasks` }
      })
      row.addEventListener('click', () => this.selectField(f.name))
    })
    this.loadValues()
  }

  // Outlier sample ids, judged WITHIN each material (mirrors the Results
  // Summary card's per-material stats) — across mixed materials the minority
  // material's whole cluster would read as "outliers" of the majority one.
  // MAD of 0 (discrete rating scales) disables flagging entirely.
  outlierIds(points) {
    const median = (arr) => {
      const s = [...arr].sort((a, b) => a - b)
      const m = Math.floor(s.length / 2)
      return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2
    }
    const byMaterial = {}
    points.forEach((p) => {
      const key = p.material || ''
      if (!byMaterial[key]) byMaterial[key] = []
      byMaterial[key].push(p)
    })
    const flagged = new Set()
    Object.values(byMaterial).forEach((group) => {
      if (group.length < 4) return
      const vals = group.map(p => p.avg)
      const med = median(vals)
      const mad = median(vals.map(v => Math.abs(v - med)))
      if (!mad) return
      group.forEach((p) => {
        if ((0.6745 * Math.abs(p.avg - med)) / mad >= 3.5) flagged.add(p.sample_id)
      })
    })
    return flagged
  }

  selectField(name) {
    if (this.field === name) return
    this.field = name
    this.stripsTarget.querySelectorAll('.m-frow').forEach((row) => {
      row.classList.toggle('sel', row.dataset.field === name)
    })
    this.loadSeq = (this.loadSeq || 0) + 1
    this.loadValues()
  }

  loadValues() {
    if (!this.field) {
      this.renderEmpty('Click a measurement above to open its trend')
      return
    }
    const p = new URLSearchParams({
      range: this.rangeTarget.value,
      task: this.taskTarget.value,
      field: this.field
    })
    this.traitFilters.forEach(f => p.append('traits[]', `${encodeURIComponent(f.traitId)}:${f.value}`))
    const qs = p.toString()
    this.fetchJson(`${this.data.get('valuesUrl')}?${qs}`, data => this.render(data))
    this.fetchJson(`${this.data.get('fieldJobsUrl')}?${qs}`, data => this.renderJobs(data))
    this.fetchJson(`${this.data.get('fieldCriteriaUrl')}?${qs}`, data => this.renderCriteria(data))
  }

  renderEmpty(msg = 'Pick a task above') {
    this.chartSubTarget.textContent = msg
    this.chartTarget.innerHTML = ''
    this.histTarget.innerHTML = ''
    this.failedTarget.innerHTML = ''
    this.critTarget.innerHTML = ''
    this.critSubTarget.textContent = ''
    this.jobsTarget.innerHTML = ''
    this.jobsSubTarget.textContent = ''
    const blank = { label: '', value: '—', delta: null, deltaText: null }
    kpiTile(this.kpiNTarget, { ...blank, label: 'Results' })
    kpiTile(this.kpiMeanTarget, { ...blank, label: 'Mean' })
    kpiTile(this.kpiSdTarget, { ...blank, label: 'Std dev' })
    kpiTile(this.kpiPassTarget, { ...blank, label: 'Pass rate' })
  }

  // Drill-in idea #2: the criteria sets judging this field's values, with
  // counts — "criteria vary" becomes inspectable instead of a shrug.
  renderCriteria(data) {
    const sets = data.criteria_sets
    this.critTarget.innerHTML = ''
    if (!sets.length) {
      this.critSubTarget.textContent = 'This measurement records values without judging them'
      const empty = document.createElement('div')
      empty.className = 'm-empty'
      empty.textContent = 'No pass/fail criteria defined on this measurement.'
      this.critTarget.appendChild(empty)
      return
    }
    const anyOverrides = sets.some(s => s.has_overrides)
    this.critSubTarget.textContent = (sets.length === 1
      ? 'All matched definitions agree — zones shown on the strip above'
      : `${sets.length} disagreeing criteria sets — zones appear when definitions agree`)
      + (anyOverrides ? ' · * carries per-material overrides' : '')
    renderTable(
      this.critTarget,
      [{ label: 'Criteria' }, { label: 'Results judged', num: true }, { label: 'Fails', num: true }],
      sets.map(s => [this.criteriaLabel(s) + (s.has_overrides ? ' *' : ''), fmtNum(s.judged), s.fails ? fmtNum(s.fails) : '—'])
    )
  }

  criteriaLabel(s) {
    const f = v => fmtNum(v, Number.isInteger(v) ? 0 : 1)
    const hasMin = s.min !== null && s.min !== undefined
    const hasMax = s.max !== null && s.max !== undefined
    if (hasMin && hasMax) return `${f(s.min)} – ${f(s.max)}`
    if (hasMin) return `≥ ${f(s.min)}`
    if (hasMax) return `≤ ${f(s.max)}`
    return '—'
  }

  // Drill-in idea #1: per-job rollup bridging to each job's tasks screen,
  // where the Results Summary card owns the within-job story.
  renderJobs(data) {
    this.jobsTarget.innerHTML = ''
    if (!data.jobs.length) {
      this.jobsSubTarget.textContent = ''
      const empty = document.createElement('div')
      empty.className = 'm-empty'
      empty.textContent = 'No jobs recorded this measurement in range.'
      this.jobsTarget.appendChild(empty)
      return
    }
    const dp = data.jobs.some(j => !Number.isInteger(j.mean)) ? 1 : 0
    const trimNote = data.total > data.jobs.length ? ` · showing ${data.jobs.length} most recent` : ''
    this.jobsSubTarget.textContent = `${fmtNum(data.total)} job${data.total === 1 ? '' : 's'} in range${trimNote} · click a job to open its tasks screen`
    renderTable(
      this.jobsTarget,
      [{ label: 'ID' }, { label: 'Job' }, { label: 'Latest' }, { label: 'Samples', num: true }, { label: 'Results', num: true }, { label: 'Mean', num: true }, { label: 'Fails', num: true }],
      data.jobs.map(j => [
        `#${j.display_id}`, j.job, new Date(j.at).toLocaleDateString(), fmtNum(j.samples), fmtNum(j.results),
        fmtNum(j.mean, dp), j.fails ? fmtNum(j.fails) : '—'
      ]),
      (ri) => { window.location.href = `/jobs/${data.jobs[ri].job_id}/tasks` }
    )
  }

  render(data) {
    if (data.mode === 'weekly') {
      this.renderWeeklyMode(data)
      return
    }
    const pts = data.points
    const vals = pts.map(p => p.value)
    const n = vals.length
    const mean = n ? vals.reduce((a, b) => a + b, 0) / n : null
    const sd = n > 1 ? Math.sqrt(vals.reduce((a, b) => a + ((b - mean) ** 2), 0) / (n - 1)) : null
    const sorted = [...vals].sort((a, b) => a - b)
    const median = n ? sorted[Math.floor((n - 1) / 2)] : null
    const passes = pts.filter(p => p.status === 'pass').length
    const fails = pts.filter(p => p.status === 'fail').length
    const tested = passes + fails
    const passPct = tested ? Math.round((100 * passes) / tested) : null
    const dp = vals.some(v => !Number.isInteger(v)) ? 1 : 0

    kpiTile(this.kpiNTarget, {
      label: 'Results',
      value: fmtNum(n),
      delta: null,
      deltaText: null,
      sub: data.truncated ? `showing newest ${fmtNum(pts.length)} (capped)` : null
    })
    kpiTile(this.kpiMeanTarget, {
      label: 'Mean',
      value: mean === null ? '—' : fmtNum(mean, dp),
      delta: null,
      deltaText: null,
      sub: median === null ? null : `median ${fmtNum(median, dp)}`
    })
    kpiTile(this.kpiSdTarget, { label: 'Std dev', value: sd === null ? '—' : fmtNum(sd, 1), delta: null, deltaText: null })
    kpiTile(this.kpiPassTarget, {
      label: 'Pass rate',
      value: passPct === null ? '—' : `${passPct}%`,
      delta: null,
      deltaText: null,
      sub: tested ? `of ${fmtNum(tested)} judged values` : 'no pass/fail criteria matched'
    })

    const refLines = []
    if (data.criteria) {
      if (data.criteria.max !== null && data.criteria.max !== undefined) refLines.push({ y: data.criteria.max, label: `Max ${fmtNum(data.criteria.max, dp)}`, kind: 'criteria' })
      if (data.criteria.min !== null && data.criteria.min !== undefined) refLines.push({ y: data.criteria.min, label: `Min ${fmtNum(data.criteria.min, dp)}`, kind: 'criteria' })
    }
    if (mean !== null) refLines.push({ y: mean, label: `x̄ ${fmtNum(mean, dp)}`, kind: 'mean' })

    this.chartSubTarget.textContent = `${this.field} · ${this.taskTarget.selectedOptions[0].textContent}${
      data.criteria ? '' : ' · no single criteria set — points still judged by their stored status'
    }${data.truncated ? ' · truncated to newest 2,000 points' : ''}`
    controlChart(this.chartTarget, pts, { refLines, dp })

    renderTable(
      this.chartTableTarget,
      [{ label: 'Date' }, { label: 'Job' }, { label: 'Value', num: true }, { label: 'Status' }],
      pts.slice(-200).reverse().map(p => [new Date(p.at).toLocaleDateString(), p.job, fmtNum(p.value, dp), p.status]),
    )

    this.histSubTarget.textContent = `${fmtNum(n)} values${data.criteria ? ' · dashed lines mark the pass/fail criteria' : ''}`
    histogram(this.histTarget, vals, { min: data.criteria && data.criteria.min, max: data.criteria && data.criteria.max, dp })

    const failedRows = pts.filter(p => p.status === 'fail').slice(-8).reverse()
    if (failedRows.length) {
      renderTable(
        this.failedTarget,
        [{ label: 'Date' }, { label: 'Job' }, { label: 'Value', num: true }],
        failedRows.map(p => [new Date(p.at).toLocaleDateString(), p.job, fmtNum(p.value, dp)]),
      )
    } else {
      this.failedTarget.innerHTML = ''
      const empty = document.createElement('div')
      empty.className = 'm-empty'
      empty.textContent = tested ? 'No failed values in range. Nice.' : 'No pass/fail criteria on this field.'
      this.failedTarget.appendChild(empty)
    }
  }

  // Drill-in idea #3: above the volume threshold the server returns weekly
  // median/quartile stats instead of points (no more truncation), and the
  // histogram arrives as server-side bins.
  renderWeeklyMode(data) {
    const s = data.summary
    const dp = data.weeks.some(w => !Number.isInteger(w.med)) ? 1 : 0

    kpiTile(this.kpiNTarget, {
      label: 'Results',
      value: fmtNum(s.n),
      delta: null,
      deltaText: null,
      sub: 'high volume — aggregated weekly'
    })
    kpiTile(this.kpiMeanTarget, {
      label: 'Mean',
      value: s.mean === null ? '—' : fmtNum(s.mean, dp),
      delta: null,
      deltaText: null,
      sub: s.median === null ? null : `median ${fmtNum(s.median, dp)}`
    })
    kpiTile(this.kpiSdTarget, { label: 'Std dev', value: s.sd === null ? '—' : fmtNum(s.sd, 1), delta: null, deltaText: null })
    const tested = s.passes + s.fails
    kpiTile(this.kpiPassTarget, {
      label: 'Pass rate',
      value: tested ? `${Math.round((100 * s.passes) / tested)}%` : '—',
      delta: null,
      deltaText: null,
      sub: tested ? `of ${fmtNum(tested)} judged values` : 'no pass/fail criteria matched'
    })

    const refLines = []
    if (data.criteria) {
      if (data.criteria.max !== null && data.criteria.max !== undefined) refLines.push({ y: data.criteria.max, label: `Max ${fmtNum(data.criteria.max, dp)}`, kind: 'criteria' })
      if (data.criteria.min !== null && data.criteria.min !== undefined) refLines.push({ y: data.criteria.min, label: `Min ${fmtNum(data.criteria.min, dp)}`, kind: 'criteria' })
    }
    if (s.mean !== null) refLines.push({ y: s.mean, label: `x̄ ${fmtNum(s.mean, dp)}`, kind: 'mean' })

    this.chartSubTarget.textContent = `${this.field} · ${this.taskTarget.selectedOptions[0].textContent} · ${fmtNum(s.n)} results — weekly median + middle-50% whiskers, red dot = week with fails${
      data.criteria ? '' : ' · no single criteria set — values still judged by their stored status'
    }`
    weeklyWhiskers(this.chartTarget, data.weeks, { refLines, dp })

    renderTable(
      this.chartTableTarget,
      [{ label: 'Week' }, { label: 'Results', num: true }, { label: 'Median', num: true }, { label: 'Q1', num: true }, { label: 'Q3', num: true }, { label: 'Min', num: true }, { label: 'Max', num: true }, { label: 'Fails', num: true }],
      data.weeks.map(w => [
        `Week of ${new Date(w.week).toLocaleDateString()}`, fmtNum(w.n), fmtNum(w.med, dp),
        fmtNum(w.q1, dp), fmtNum(w.q3, dp), fmtNum(w.min, dp), fmtNum(w.max, dp),
        w.fails ? fmtNum(w.fails) : '—'
      ]),
    )

    this.histSubTarget.textContent = `${fmtNum(s.n)} values, binned server-side${data.criteria ? ' · dashed lines mark the pass/fail criteria' : ''}`
    binsHistogram(this.histTarget, data.bins, { min: data.criteria && data.criteria.min, max: data.criteria && data.criteria.max, dp })

    if (data.recent_fails.length) {
      renderTable(
        this.failedTarget,
        [{ label: 'Date' }, { label: 'Job' }, { label: 'Value', num: true }],
        data.recent_fails.map(p => [new Date(p.at).toLocaleDateString(), p.job, fmtNum(p.value, dp)]),
      )
    } else {
      this.failedTarget.innerHTML = ''
      const empty = document.createElement('div')
      empty.className = 'm-empty'
      empty.textContent = tested ? 'No failed values in range. Nice.' : 'No pass/fail criteria on this field.'
      this.failedTarget.appendChild(empty)
    }
  }

  toggleTable(ev) {
    const btn = ev.currentTarget
    const card = btn.closest('.m-card')
    const showing = card.classList.toggle('show-table')
    btn.textContent = showing ? 'Chart' : 'Table'
  }
}
